import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { StatusBadge, UIButton } from '../../../features/ui-system';

const toneByState = (state) => {
  const token = String(state || '').trim().toLowerCase();
  if (token === 'offline') return 'danger';
  if (token === 'degraded') return 'warn';
  if (token === 'warning') return 'info';
  return 'neutral';
};

const labelByState = (state) => {
  const token = String(state || '').trim().toLowerCase();
  if (token === 'offline') return 'offline';
  if (token === 'degraded') return 'degraded';
  if (token === 'warning') return 'warning';
  return 'unknown';
};

export default function CustomerImpactDrawer({
  open,
  onClose,
  title = 'Daftar Customer Terdampak',
  subtitle = '',
  customers = [],
  recoveryIncidentId = '',
}) {
  const [stateFilter, setStateFilter] = useState('all');

  const filtered = useMemo(() => {
    if (stateFilter === 'all') return customers;
    return customers.filter((customer) => String(customer?.state || '').toLowerCase() === stateFilter);
  }, [customers, stateFilter]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[950] flex justify-end bg-black/60" role="dialog" aria-modal="true">
      <div className="h-full w-full max-w-[420px] border-l border-white/10 bg-[#070f1c] p-4 shadow-2xl">
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-white">{title}</p>
            {subtitle ? <p className="mt-1 text-xs text-slate-400">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-white/15 p-1 text-slate-300 hover:bg-white/10"
            aria-label="Tutup"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-1.5">
          {['all', 'offline', 'degraded', 'warning'].map((token) => (
            <button
              key={token}
              type="button"
              onClick={() => setStateFilter(token)}
              className={`rounded-md border px-2 py-1 text-[11px] ${stateFilter === token ? 'border-cyan-300/60 bg-cyan-500/10 text-cyan-100' : 'border-white/10 bg-[#0d1728] text-slate-300'}`}
            >
              {token}
            </button>
          ))}
          <span className="ml-auto text-[11px] text-slate-500">{filtered.length} item</span>
        </div>

        <div className="max-h-[calc(100%-110px)] space-y-2 overflow-auto pr-1">
          {filtered.length === 0 ? (
            <p className="rounded-md border border-white/10 bg-[#0d1728] px-3 py-2 text-xs text-slate-400">
              Tidak ada customer untuk filter ini.
            </p>
          ) : filtered.map((customer) => (
            <div key={customer.id} className="rounded-md border border-white/10 bg-[#0d1728] px-2.5 py-2">
              <div className="mb-1 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-white">{customer.name} ({customer.code})</p>
                <StatusBadge tone={toneByState(customer.state)}>{labelByState(customer.state)}</StatusBadge>
              </div>
              <p className="text-[11px] text-slate-400">Site: {customer.site || '-'}</p>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <UIButton as="link" to={`/customers?customerId=${encodeURIComponent(String(customer.id || ''))}`} variant="ghost">
                  View Customer
                </UIButton>
                {recoveryIncidentId ? (
                  <UIButton
                    as="link"
                    to={`/recovery?incidentId=${encodeURIComponent(String(recoveryIncidentId))}&customerId=${encodeURIComponent(String(customer.id || ''))}`}
                    variant="ghost"
                  >
                    Open in Recovery
                  </UIButton>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
